import type { BridgePrompt } from '@entangled-vega/shared';

import type { RelationshipLens } from '../data/graph-types.js';
import type { GraphIndex, GraphPath } from '../lib/graph.js';
import type { NoPathRecovery } from '../lib/no-path-recovery.js';
import {
  groupSourcesForDisplay,
  relationshipProvenanceLabel,
  sourcesForRelationship,
} from '../lib/provenance.js';
import { relationshipLabelForPerson } from '../lib/relationship-view.js';
import { relationshipDateLabel } from '../lib/relationship-time.js';
import type { AlternativeDocumentedPath } from '../lib/story-graph-models.js';
import { FeaturedGuess, guessMatchesStepCount, type StepGuessRange } from './FeaturedGuess.js';
import { RouteChoices } from './RouteChoices.js';
import { StoryPlayer } from './StoryPlayer.js';

type TraceBannerProps = {
  from: string | null;
  to: string | null;
  path: GraphPath | null;
  primaryPath: GraphPath | null;
  alternatives: readonly AlternativeDocumentedPath[];
  recovery: NoPathRecovery | null;
  featured: boolean;
  concealed: boolean;
  selectedGuess: StepGuessRange | null;
  bridgePrompt: BridgePrompt | null;
  selectedBridgeChoiceId: string | null;
  featuredAnswerSteps: number | null;
  playbackYieldKey: string;
  index: GraphIndex;
  snapshotDate: string;
  enabledLenses: readonly RelationshipLens[];
  shareFeedback: string | null;
  canShare: boolean;
  favorite: boolean;
  onClear: () => void;
  onCopy: () => void;
  onShare: () => void;
  onSaveSnapshot: () => void;
  onToggleFavorite: () => void;
  onRetryAllLenses: () => void;
  onTryConnection: (from: string, to: string) => void;
  onSelectPerson: (personId: string) => void;
  onGuess: (guess: StepGuessRange) => void;
  onBridgeGuess: (personId: string) => void;
  onReveal: () => void;
  onChooseRoute: (path: GraphPath) => void;
  onFocusStep: (sourceId: string, targetId: string) => void;
  onPathComplete: (path: GraphPath) => void;
  onSaveCard: () => void;
};

function lensLabel(lens: RelationshipLens): string {
  return lens === 'personal' ? 'Personal' : 'Family';
}

export function TraceBanner({
  from,
  to,
  path,
  primaryPath,
  alternatives,
  recovery,
  featured,
  concealed,
  selectedGuess,
  bridgePrompt,
  selectedBridgeChoiceId,
  featuredAnswerSteps,
  playbackYieldKey,
  index,
  snapshotDate,
  enabledLenses,
  shareFeedback,
  canShare,
  favorite,
  onClear,
  onCopy,
  onShare,
  onSaveSnapshot,
  onToggleFavorite,
  onRetryAllLenses,
  onTryConnection,
  onSelectPerson,
  onGuess,
  onBridgeGuess,
  onReveal,
  onChooseRoute,
  onFocusStep,
  onPathComplete,
  onSaveCard,
}: TraceBannerProps) {
  if (from === null && to === null) {
    return null;
  }
  const fromName = from === null ? 'Choose a start' : (index.peopleById.get(from)?.name ?? from);
  const toName = to === null ? 'Choose an end' : (index.peopleById.get(to)?.name ?? to);
  const endpoints = from !== null && to !== null ? { from, to } : null;
  const pathSourceCount =
    path === null
      ? 0
      : new Set(
          path.relationships.flatMap((relationship) =>
            sourcesForRelationship(relationship, index).map((source) => source.url),
          ),
        ).size;
  const traceStatus =
    endpoints === null
      ? `${fromName} selected. Choose the other trace endpoint.`
      : concealed
        ? `A featured relationship path from ${fromName} to ${toName} is ready to reveal.`
        : path === null
          ? `No path in this snapshot from ${fromName} to ${toName} using ${enabledLenses.map(lensLabel).join(' and ')}.`
          : `Relationship path found from ${fromName} to ${toName} in ${path.relationships.length} ${path.relationships.length === 1 ? 'step' : 'steps'}.`;

  return (
    <section className="trace-banner" aria-label="Active connection trace">
      <p className="sr-only" role="status" aria-live="polite">
        {traceStatus}
      </p>
      <div className="trace-banner__heading">
        <div>
          <p className="eyebrow">
            {featured
              ? 'Featured relationship path'
              : primaryPath === path
                ? 'Preferred relationship path'
                : 'Alternative relationship path'}
          </p>
          <h2>
            {fromName} <span aria-hidden="true">→</span> {toName}
            {path === null || endpoints === null || concealed ? null : (
              <span className="trace-banner__steps">
                {' '}
                {path.relationships.length} {path.relationships.length === 1 ? 'step' : 'steps'}
              </span>
            )}
          </h2>
          <p className="trace-banner__lenses">Using {enabledLenses.map(lensLabel).join(' + ')}</p>
        </div>
        <div className="trace-banner__actions">
          <button className="button button--small" type="button" onClick={onClear}>
            Clear
          </button>
        </div>
      </div>
      {shareFeedback === null ? null : (
        <p className="trace-banner__feedback" role="status">
          {shareFeedback}
        </p>
      )}
      {endpoints !== null ? (
        concealed && path !== null ? (
          <FeaturedGuess
            fromName={fromName}
            toName={toName}
            selectedGuess={selectedGuess}
            bridgePrompt={bridgePrompt}
            selectedBridgeChoiceId={selectedBridgeChoiceId}
            onGuess={onGuess}
            onBridgeGuess={onBridgeGuess}
            onReveal={onReveal}
          />
        ) : path === null ? (
          <div className="trace-banner__empty">
            <p>
              No path was found in this data snapshot using the enabled relationship lenses. That
              does not mean no real-world connection exists.
            </p>
            {recovery === null ? null : (
              <div className="trace-recovery">
                <p className="trace-recovery__component">
                  {recovery.separateUnderAllLenses
                    ? 'These people are in separate connected components in this data snapshot, even with Personal and Family enabled.'
                    : 'A relationship path is available when both Personal and Family are enabled.'}
                </p>
                {recovery.retryWithAllLenses ? (
                  <button className="button button--small" type="button" onClick={onRetryAllLenses}>
                    Retry with Personal + Family
                  </button>
                ) : null}
                <div className="trace-recovery__endpoints">
                  {(
                    [
                      { endpoint: recovery.from, side: 'from' },
                      { endpoint: recovery.to, side: 'to' },
                    ] as const
                  ).map(({ endpoint, side }) => (
                    <section key={endpoint.id} className="trace-recovery__endpoint">
                      <h3>{endpoint.name}</h3>
                      <p>
                        {endpoint.connectionCount.toLocaleString('en-US')} source-linked{' '}
                        {endpoint.connectionCount === 1 ? 'connection' : 'connections'} in the
                        enabled lenses
                      </p>
                      {endpoint.neighbors.length === 0 ? (
                        <p>No direct continuation appears in this snapshot.</p>
                      ) : (
                        <div className="trace-recovery__chips">
                          {endpoint.neighbors.map((neighbor) => {
                            const candidateFrom = side === 'from' ? endpoint.id : neighbor.id;
                            const candidateTo = side === 'from' ? neighbor.id : endpoint.id;
                            return (
                              <button
                                key={neighbor.id}
                                className="button button--small"
                                type="button"
                                aria-label={`Try ${index.peopleById.get(candidateFrom)?.name ?? candidateFrom} to ${index.peopleById.get(candidateTo)?.name ?? candidateTo}`}
                                onClick={() => onTryConnection(candidateFrom, candidateTo)}
                              >
                                Try {neighbor.name}
                                {neighbor.context ? <small> · context</small> : null}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </section>
                  ))}
                </div>
                {recovery.fallbackPeople.length === 0 ? null : (
                  <div className="trace-recovery__fallback">
                    <p>Keep exploring another connected person in this snapshot:</p>
                    <div className="trace-recovery__chips">
                      {recovery.fallbackPeople.map((person) => (
                        <button
                          key={person.id}
                          className="button button--small"
                          type="button"
                          onClick={() => onSelectPerson(person.id)}
                        >
                          Explore {person.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="trace-recovery__inspect">
              <button
                className="button button--small"
                type="button"
                onClick={() => onSelectPerson(endpoints.from)}
              >
                Inspect {fromName}
              </button>
              <button
                className="button button--small"
                type="button"
                onClick={() => onSelectPerson(endpoints.to)}
              >
                Inspect {toName}
              </button>
            </div>
          </div>
        ) : (
          <>
            {selectedGuess !== null && featuredAnswerSteps !== null ? (
              <p className="featured-guess__result" role="status">
                {guessMatchesStepCount(selectedGuess, featuredAnswerSteps)
                  ? 'Your range matches this data snapshot.'
                  : 'A good guess—this snapshot has a different path length.'}{' '}
                The revealed path has {featuredAnswerSteps}{' '}
                {featuredAnswerSteps === 1 ? 'step' : 'steps'}.
              </p>
            ) : null}
            {bridgePrompt === null || selectedBridgeChoiceId === null ? null : (
              <p className="featured-guess__result" role="status">
                {selectedBridgeChoiceId === bridgePrompt.answerId
                  ? 'Your bridge choice appears in this accepted path.'
                  : `The accepted bridge answer is ${
                      bridgePrompt.choices.find(({ id }) => id === bridgePrompt.answerId)?.name ??
                      'shown in the path'
                    }.`}
              </p>
            )}
            {primaryPath === null ? null : (
              <RouteChoices
                primaryPath={primaryPath}
                alternatives={alternatives}
                selectedPath={path}
                index={index}
                onChoose={onChooseRoute}
              />
            )}
            {path.relationships.length === 0 ? null : (
              <StoryPlayer
                path={path}
                index={index}
                snapshotDate={snapshotDate}
                featured={featured}
                yieldKey={playbackYieldKey}
                onFocusStep={onFocusStep}
                onComplete={onPathComplete}
              />
            )}
            {path.relationships.length === 0 ? null : (
              <section className="trace-completion" aria-label="Completed trace">
                <div className="trace-completion__heading">
                  <div>
                    <p className="eyebrow">Completed trace</p>
                    <h3>
                      {fromName} to {toName}
                    </h3>
                  </div>
                  <div className="trace-completion__actions" aria-label="Share completed trace">
                    <button className="button button--small" type="button" onClick={onCopy}>
                      Copy canonical link
                    </button>
                    {canShare ? (
                      <button className="button button--small" type="button" onClick={onShare}>
                        Share
                      </button>
                    ) : null}
                    <button className="button button--small" type="button" onClick={onSaveSnapshot}>
                      Save high-resolution map
                    </button>
                    <button className="button button--small" type="button" onClick={onSaveCard}>
                      Save story card
                    </button>
                    <button
                      className="button button--small"
                      type="button"
                      aria-pressed={favorite}
                      onClick={onToggleFavorite}
                    >
                      {favorite ? 'Remove from Saved' : 'Save connection'}
                    </button>
                  </div>
                </div>
                <ol className="trace-completion__steps">
                  {path.relationships.map((relationship, relationshipIndex) => {
                    const sourceId = path.people[relationshipIndex];
                    const targetId = path.people[relationshipIndex + 1];
                    const sourceName =
                      sourceId === undefined
                        ? 'Unknown person'
                        : (index.peopleById.get(sourceId)?.name ?? sourceId);
                    const targetName =
                      targetId === undefined
                        ? 'Unknown person'
                        : (index.peopleById.get(targetId)?.name ?? targetId);
                    const sources = sourcesForRelationship(relationship, index);
                    const sourceGroups = groupSourcesForDisplay(sources);
                    const dateLabel = relationshipDateLabel(relationship, snapshotDate);
                    return (
                      <li key={relationship.id}>
                        <div className="trace-completion__people">
                          <span>Step {relationshipIndex + 1}</span>
                          <button
                            type="button"
                            disabled={sourceId === undefined}
                            onClick={() => {
                              if (sourceId !== undefined) onSelectPerson(sourceId);
                            }}
                          >
                            {sourceName}
                          </button>
                          <span aria-hidden="true">→</span>
                          <button
                            type="button"
                            disabled={targetId === undefined}
                            onClick={() => {
                              if (targetId !== undefined) onSelectPerson(targetId);
                            }}
                          >
                            {targetName}
                          </button>
                        </div>
                        <p>
                          <strong>
                            {sourceId === undefined
                              ? relationship.label
                              : relationshipLabelForPerson(relationship, sourceId)}
                          </strong>
                          {dateLabel === null ? null : <span>{dateLabel}</span>}
                          <span>{relationshipProvenanceLabel(relationship)}</span>
                        </p>
                        <div className="trace-completion__step-actions">
                          {sourceId === undefined || targetId === undefined ? null : (
                            <button
                              className="button button--small"
                              type="button"
                              onClick={() => onFocusStep(sourceId, targetId)}
                            >
                              Focus edge
                            </button>
                          )}
                          <details>
                            <summary>Sources ({sources.length.toLocaleString('en-US')})</summary>
                            <div>
                              {sourceGroups.map((group) => (
                                <span key={group.label}>
                                  <strong>{group.label}</strong>
                                  {group.links.map((link) => (
                                    <a
                                      key={link.id}
                                      href={link.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {link.label}
                                    </a>
                                  ))}
                                </span>
                              ))}
                            </div>
                          </details>
                        </div>
                      </li>
                    );
                  })}
                </ol>
                <p className="trace-completion__note">
                  Source-linked public information; no depicted person or source endorses Entangled
                  Vega.
                </p>
              </section>
            )}
            <ol className="trace-banner__path" aria-label="People in this path">
              {path.people.map((personId, indexInPath) => (
                <li key={personId}>
                  <button type="button" onClick={() => onSelectPerson(personId)}>
                    {index.peopleById.get(personId)?.name ?? personId}
                  </button>
                  {indexInPath === path.people.length - 1 ? null : (
                    <span aria-hidden="true">→</span>
                  )}
                </li>
              ))}
            </ol>
            {path.relationships.length === 0 ? null : (
              <details className="trace-evidence">
                <summary>
                  Evidence for {path.relationships.length} path{' '}
                  {path.relationships.length === 1 ? 'step' : 'steps'} ·{' '}
                  {pathSourceCount.toLocaleString('en-US')}{' '}
                  {pathSourceCount === 1 ? 'source' : 'sources'}
                </summary>
                <ol>
                  {path.relationships.map((relationship, relationshipIndex) => {
                    const sourceId = path.people[relationshipIndex];
                    const targetId = path.people[relationshipIndex + 1];
                    const sourceName =
                      sourceId === undefined
                        ? 'Unknown person'
                        : (index.peopleById.get(sourceId)?.name ?? sourceId);
                    const targetName =
                      targetId === undefined
                        ? 'Unknown person'
                        : (index.peopleById.get(targetId)?.name ?? targetId);
                    const connection = index.connectionByRelationshipId.get(relationship.id);
                    const representative = connection?.representative ?? relationship;
                    const phases = connection?.phases ?? [relationship];
                    return (
                      <li key={connection?.key ?? relationship.id}>
                        <p className="trace-evidence__connection">
                          <button
                            type="button"
                            disabled={sourceId === undefined}
                            onClick={() => {
                              if (sourceId !== undefined) {
                                onSelectPerson(sourceId);
                              }
                            }}
                          >
                            {sourceName}
                          </button>
                          <span aria-hidden="true">—</span>
                          <strong>
                            {sourceId === undefined
                              ? representative.label
                              : relationshipLabelForPerson(representative, sourceId)}
                          </strong>
                          <span aria-hidden="true">→</span>
                          <button
                            type="button"
                            disabled={targetId === undefined}
                            onClick={() => {
                              if (targetId !== undefined) {
                                onSelectPerson(targetId);
                              }
                            }}
                          >
                            {targetName}
                          </button>
                          {sourceId === undefined || targetId === undefined ? null : (
                            <button
                              className="button button--small"
                              type="button"
                              onClick={() => onFocusStep(sourceId, targetId)}
                            >
                              Focus edge
                            </button>
                          )}
                        </p>
                        <ol
                          className="trace-evidence__phases"
                          aria-label={`${phases.length} retained relationship ${phases.length === 1 ? 'record' : 'phases'}`}
                        >
                          {phases.map((phase) => {
                            const sources = sourcesForRelationship(phase, index);
                            const sourceGroups = groupSourcesForDisplay(sources);
                            const sourceCount = sourceGroups.reduce(
                              (total, group) => total + group.links.length,
                              0,
                            );
                            const dateLabel = relationshipDateLabel(phase, snapshotDate);
                            return (
                              <li key={phase.id}>
                                <p className="trace-evidence__meta">
                                  <strong>
                                    {sourceId === undefined
                                      ? phase.label
                                      : relationshipLabelForPerson(phase, sourceId)}
                                  </strong>
                                  <span>{lensLabel(phase.lens)}</span>
                                  {dateLabel === null ? null : <span>{dateLabel}</span>}
                                  <span>{relationshipProvenanceLabel(phase)}</span>
                                </p>
                                <details className="trace-evidence__sources">
                                  <summary>Sources ({sourceCount.toLocaleString('en-US')})</summary>
                                  {sourceCount === 0 ? (
                                    <em>Source metadata unavailable</em>
                                  ) : (
                                    <div>
                                      {sourceGroups.map((group) => (
                                        <span key={group.label}>
                                          <strong>{group.label}</strong>
                                          {group.links.map((link) => (
                                            <a
                                              key={link.id}
                                              href={link.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              {link.label}
                                            </a>
                                          ))}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </details>
                              </li>
                            );
                          })}
                        </ol>
                      </li>
                    );
                  })}
                </ol>
              </details>
            )}
          </>
        )
      ) : (
        <p className="trace-banner__empty">
          Choose the other person above to complete this connection.
        </p>
      )}
    </section>
  );
}
